import { z } from 'zod'

import type { ZodInfer } from '@mntn-dev/utility-types'

import {
  archiveMimeTypes,
  audioMimeTypes,
  codeMimeTypes,
  documentMimeTypes,
  fontMimeTypes,
  imageDocumentMimeTypes,
  imageMimeTypes,
  otherMimeTypes,
  videoMimeTypes,
} from './mime-data.ts'
import { MimeTypeBuilder } from './mime-type-builder.ts'

/**
 * Document File Content Types as Extensions
 */
export const [
  documentFileContentTypes,
  documentFileExtensions,
  documentFileContentSchema,
  documentFileExtensionSchema,
] = MimeTypeBuilder(documentMimeTypes)
export type DocumentContentType = ZodInfer<typeof documentFileContentSchema>
export type DocumentExtension = ZodInfer<typeof documentFileExtensionSchema>

/**
 * Image File Content Types as Extensions
 */
export const [
  imageFileContentTypes,
  imageFileExtensions,
  imageFileContentSchema,
  imageExtensionsSchema,
] = MimeTypeBuilder(imageMimeTypes)
export type ImageContentType = ZodInfer<typeof imageFileContentSchema>
export type ImageExtension = ZodInfer<typeof imageExtensionsSchema>

/**
 * Image Document File Content Types as Extensions
 */
export const [
  imageDocumentFileContentTypes,
  imageDocumentFileExtensions,
  imageDocumentFileContentSchema,
  imageDocumentExtensionsSchema,
] = MimeTypeBuilder(imageDocumentMimeTypes)
export type ImageDocumentContentType = ZodInfer<
  typeof imageDocumentFileContentSchema
>
export type ImageDocumentExtension = ZodInfer<
  typeof imageDocumentExtensionsSchema
>

/**
 * Audio File Content Types as Extensions
 */
export const [
  audioFileContentTypes,
  audioFileExtensions,
  audioFileContentSchema,
  audioExtensionsSchema,
] = MimeTypeBuilder(audioMimeTypes)
export type AudioContentType = ZodInfer<typeof audioFileContentSchema>
export type AudioExtension = ZodInfer<typeof audioExtensionsSchema>

/**
 * Video File Content Types as Extensions
 */
export const [
  videoFileContentTypes,
  videoFileExtensions,
  videoFileContentSchema,
  videoExtensionsSchema,
] = MimeTypeBuilder(videoMimeTypes)
export type VideoContentType = ZodInfer<typeof videoFileContentSchema>
export type VideoExtension = ZodInfer<typeof videoExtensionsSchema>

/**
 * Archive File Content Types as Extensions
 */
export const [
  archiveFileContentTypes,
  archiveFileExtensions,
  archiveFileContentSchema,
  archiveExtensionsSchema,
] = MimeTypeBuilder(archiveMimeTypes)
export type ArchiveContentType = ZodInfer<typeof archiveFileContentSchema>
export type ArchiveExtension = ZodInfer<typeof archiveExtensionsSchema>

/**
 * Font File Content Types as Extensions
 */
export const [
  fontFileContentTypes,
  fontFileExtensions,
  fontFileContentSchema,
  fontExtensionsSchema,
] = MimeTypeBuilder(fontMimeTypes)
export type FontContentType = ZodInfer<typeof fontFileContentSchema>
export type FontExtension = ZodInfer<typeof fontExtensionsSchema>

/**
 * Code File Content Types as Extensions
 */
export const [
  codeFileContentTypes,
  codeFileExtensions,
  codeFileContentSchema,
  codeExtensionsSchema,
] = MimeTypeBuilder(codeMimeTypes)
export type CodeContentType = ZodInfer<typeof codeFileContentSchema>
export type CodeExtension = ZodInfer<typeof codeExtensionsSchema>

/**
 * Other File Content Types as Extensions
 */
export const [
  otherFileContentTypes,
  otherFileExtensions,
  otherFileContentSchema,
  otherExtensionsSchema,
] = MimeTypeBuilder(otherMimeTypes)
export type OtherContentType = ZodInfer<typeof otherFileContentSchema>
export type OtherExtension = ZodInfer<typeof otherExtensionsSchema>

/**
 * Combined Content Types as Extensions
 */

export const FileContentTypeSchema = z.enum([
  ...archiveFileContentTypes,
  ...audioFileContentTypes,
  ...codeFileContentTypes,
  ...documentFileContentTypes,
  ...fontFileContentTypes,
  ...imageFileContentTypes,
  ...otherFileContentTypes,
  ...videoFileContentTypes,
  ...imageDocumentFileContentTypes,
])
export type FileContentType = ZodInfer<typeof FileContentTypeSchema>

/**
 * Combined File Extensions
 */
export const FileExtensionSchema = z.enum([
  ...archiveFileExtensions,
  ...audioFileExtensions,
  ...codeFileExtensions,
  ...documentFileExtensions,
  ...fontFileExtensions,
  ...imageFileExtensions,
  ...otherFileExtensions,
  ...videoFileExtensions,
  ...imageDocumentFileExtensions,
])
export type FileExtension = ZodInfer<typeof FileExtensionSchema>
